import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import FeatureGate from '../components/FeatureGate';
import { useAuth } from '../contexts/AuthContext';
import { useGuardian } from '../contexts/GuardianContext';
import supabase from '../supabase/supabase';

const {
  FiSearch,
  FiFilter,
  FiHeart,
  FiMail,
  FiPhone,
  FiMapPin,
  FiStar,
  FiPlus,
  FiInfo,
  FiCheckCircle,
  FiAlertCircle,
  FiEdit3,
  FiX,
  FiSave,
  FiExternalLink,
} = FiIcons;

function SchoolDirectory() {
  const { user } = useAuth();
  const { getActiveUserId } = useGuardian();
  const [searchQuery, setSearchQuery] = useState('');
  const [filterDivision, setFilterDivision] = useState('all');
  const [showAddModal, setShowAddModal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState({ type: '', text: '' });

  const [featuredSchools, setFeaturedSchools] = useState([]);
  const [customSchools, setCustomSchools] = useState([]);
  const [savedSchoolIds, setSavedSchoolIds] = useState(new Set());

  const [customSchoolForm, setCustomSchoolForm] = useState({
    name: '',
    division: '',
    conference: '',
    location: '',
    coach: '',
    email: '',
    phone: '',
    address: '',
    staff_directory_url: '',
  });

  useEffect(() => {
    loadSchools();
    // Listen for updates from CollegeTracker
    const handleSchoolsUpdated = () => {
      loadSchools();
    };
    window.addEventListener('schoolsUpdated', handleSchoolsUpdated);
    return () => window.removeEventListener('schoolsUpdated', handleSchoolsUpdated);
  }, [user]);

  const loadSchools = async () => {
    try {
      setLoading(true);
      const activeUserId = getActiveUserId();

      // Load featured schools
      const { data: featured, error: featuredError } = await supabase
        .from('featured_schools')
        .select('*')
        .eq('is_active', true);

      if (featuredError) throw featuredError;
      setFeaturedSchools(featured || []);

      // Load user's custom schools
      const { data: custom, error: customError } = await supabase
        .from('user_schools')
        .select('*')
        .eq('user_id', activeUserId);

      if (customError) throw customError;
      setCustomSchools(custom || []);

      // Load saved schools
      const { data: saved, error: savedError } = await supabase
        .from('saved_schools')
        .select('school_type, school_id')
        .eq('user_id', activeUserId);

      if (savedError) throw savedError;

      const savedIds = new Set(
        (saved || []).map((s) => `${s.school_type}-${s.school_id}`)
      );
      setSavedSchoolIds(savedIds);
    } catch (error) {
      console.error('Error loading schools:', error);
      setMessage({ type: 'error', text: 'Failed to load schools' });
    } finally {
      setLoading(false);
    }
  };

  const handleToggleFavorite = async (school, schoolType) => {
    try {
      const activeUserId = getActiveUserId();
      const compositeId = `${schoolType}-${school.id}`;

      if (savedSchoolIds.has(compositeId)) {
        // Remove from favorites
        const { error } = await supabase
          .from('saved_schools')
          .delete()
          .eq('user_id', activeUserId)
          .eq('school_type', schoolType)
          .eq('school_id', school.id);

        if (error) throw error;

        setSavedSchoolIds((prev) => {
          const newSet = new Set(prev);
          newSet.delete(compositeId);
          return newSet;
        });
      } else {
        // Add to favorites
        const { error } = await supabase.from('saved_schools').insert({
          user_id: activeUserId,
          school_type: schoolType,
          school_id: school.id,
        });

        if (error) throw error;

        setSavedSchoolIds((prev) => new Set(prev).add(compositeId));
      }

      // Trigger event for CollegeTracker to refresh
      window.dispatchEvent(new Event('schoolsUpdated'));
    } catch (error) {
      console.error('Error toggling favorite:', error);
      setMessage({ type: 'error', text: 'Failed to update favorites' });
    }
  };

  const handleAddCustomSchool = async () => {
    if (!customSchoolForm.name) {
      setMessage({ type: 'error', text: 'Please enter a school name' });
      return;
    }

    try {
      setSaving(true);
      const activeUserId = getActiveUserId();

      const { data, error } = await supabase
        .from('user_schools')
        .insert({
          user_id: activeUserId,
          ...customSchoolForm,
        })
        .select()
        .single();

      if (error) throw error;

      setCustomSchools([...customSchools, data]);
      setShowAddModal(false);
      setCustomSchoolForm({
        name: '',
        division: '',
        conference: '',
        location: '',
        coach: '',
        email: '',
        phone: '',
        address: '',
        staff_directory_url: '',
      });
      setMessage({ type: 'success', text: 'School added successfully!' });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Error adding school:', error);
      setMessage({ type: 'error', text: 'Failed to add school' });
    } finally {
      setSaving(false);
    }
  };

  const allSchools = [
    ...featuredSchools.map((s) => ({ ...s, schoolType: 'featured' })),
    ...customSchools.map((s) => ({ ...s, schoolType: 'custom' })),
  ];

  const filteredSchools = allSchools.filter((school) => {
    const matchesSearch =
      school.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      (school.location && school.location.toLowerCase().includes(searchQuery.toLowerCase())) ||
      (school.coach && school.coach.toLowerCase().includes(searchQuery.toLowerCase()));

    const matchesDivision =
      filterDivision === 'all' ||
      (school.division && school.division.toLowerCase().includes(filterDivision.toLowerCase()));

    return matchesSearch && matchesDivision;
  });

  return (
    <FeatureGate feature="School Directory">
      <div className="min-h-screen bg-gray-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-3xl font-space-grotesk font-bold text-secondary mb-2">
              School Directory
            </h1>
            <p className="text-gray-600">
              Browse featured schools or add your own custom schools to track.
            </p>
          </div>

          {/* Success/Error Message */}
          {message.text && (
            <div
              className={`mb-6 p-4 rounded-lg flex items-center space-x-2 ${
                message.type === 'success'
                  ? 'bg-green-50 text-green-800'
                  : 'bg-red-50 text-red-800'
              }`}
            >
              <SafeIcon
                icon={message.type === 'success' ? FiCheckCircle : FiAlertCircle}
                className="w-5 h-5"
              />
              <span>{message.text}</span>
            </div>
          )}

          {/* Search and Filters */}
          <div className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 mb-8">
            <div className="flex flex-col md:flex-row gap-4">
              <div className="flex-1 relative">
                <SafeIcon
                  icon={FiSearch}
                  className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5"
                />
                <input
                  type="text"
                  placeholder="Search schools, locations, or coaches..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                />
              </div>
              <select
                value={filterDivision}
                onChange={(e) => setFilterDivision(e.target.value)}
                className="px-4 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
              >
                <option value="all">All Divisions</option>
                <option value="division i">Division I</option>
                <option value="division ii">Division II</option>
                <option value="division iii">Division III</option>
                <option value="naia">NAIA</option>
                <option value="juco">Junior College</option>
              </select>
              <button
                onClick={() => setShowAddModal(true)}
                className="bg-primary text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors btn-primary flex items-center space-x-2"
              >
                <SafeIcon icon={FiPlus} className="w-4 h-4" />
                <span>Add School</span>
              </button>
            </div>
          </div>

          {/* Schools Grid */}
          {loading ? (
            <div className="flex items-center justify-center py-12">
              <div className="text-center">
                <div className="w-12 h-12 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
                <p className="text-gray-600">Loading schools...</p>
              </div>
            </div>
          ) : filteredSchools.length === 0 ? (
            <div className="bg-white rounded-xl p-12 shadow-sm border border-gray-100 text-center">
              <SafeIcon icon={FiInfo} className="w-16 h-16 text-gray-300 mx-auto mb-4" />
              <h3 className="text-xl font-space-grotesk font-semibold text-secondary mb-2">
                No Schools Found
              </h3>
              <p className="text-gray-600 mb-6">
                {searchQuery || filterDivision !== 'all'
                  ? 'Try adjusting your search or filters'
                  : 'Get started by adding your first school'}
              </p>
              <button
                onClick={() => setShowAddModal(true)}
                className="bg-primary text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors btn-primary"
              >
                Add Your First School
              </button>
            </div>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filteredSchools.map((school, index) => {
                const compositeId = `${school.schoolType}-${school.id}`;
                const isSaved = savedSchoolIds.has(compositeId);

                return (
                  <motion.div
                    key={compositeId}
                    initial={{ opacity: 0, y: 30 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.05 }}
                    className="bg-white rounded-xl p-6 shadow-sm border border-gray-100 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start justify-between mb-4">
                      <div className="flex items-start space-x-3 flex-1">
                        {school.logo ? (
                          <img
                            src={school.logo}
                            alt={school.name}
                            className="w-12 h-12 rounded-lg object-cover"
                          />
                        ) : (
                          <div className="w-12 h-12 bg-primary/10 rounded-lg flex items-center justify-center">
                            <SafeIcon icon={FiStar} className="w-6 h-6 text-primary" />
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <h3 className="text-lg font-space-grotesk font-semibold text-secondary truncate">
                            {school.name}
                          </h3>
                          {school.division && (
                            <p className="text-sm text-gray-500">{school.division}</p>
                          )}
                        </div>
                      </div>
                      <button
                        onClick={() => handleToggleFavorite(school, school.schoolType)}
                        className={`transition-colors ${
                          isSaved
                            ? 'text-red-500 hover:text-red-600'
                            : 'text-gray-300 hover:text-red-500'
                        }`}
                        title={isSaved ? 'Remove from favorites' : 'Add to favorites'}
                      >
                        <SafeIcon
                          icon={FiHeart}
                          className={`w-6 h-6 ${isSaved ? 'fill-current' : ''}`}
                        />
                      </button>
                    </div>

                    {school.location && (
                      <div className="flex items-center space-x-2 text-sm text-gray-600 mb-3">
                        <SafeIcon icon={FiMapPin} className="w-4 h-4" />
                        <span>{school.location}</span>
                      </div>
                    )}

                    {school.coach && (
                      <div className="bg-gray-50 rounded-lg p-3 mb-3">
                        <p className="text-sm font-medium text-gray-700 mb-1">
                          {school.coach}
                        </p>
                        <div className="space-y-1">
                          {school.email && (
                            <div className="flex items-center space-x-2 text-xs text-gray-600">
                              <SafeIcon icon={FiMail} className="w-3 h-3" />
                              <span className="truncate">{school.email}</span>
                            </div>
                          )}
                          {school.phone && (
                            <div className="flex items-center space-x-2 text-xs text-gray-600">
                              <SafeIcon icon={FiPhone} className="w-3 h-3" />
                              <span>{school.phone}</span>
                            </div>
                          )}
                        </div>
                      </div>
                    )}

                    <div className="flex items-center space-x-2">
                      {school.email && (
                        <a
                          href={`mailto:${school.email}`}
                          className="flex-1 bg-primary text-white py-2 px-3 rounded-lg hover:bg-blue-700 transition-colors text-sm font-medium text-center"
                        >
                          Contact
                        </a>
                      )}
                      {school.staff_directory_url && (
                        <a
                          href={school.staff_directory_url}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="bg-gray-100 text-gray-600 px-3 py-2 rounded-lg hover:bg-gray-200 transition-colors"
                          title="View staff directory"
                        >
                          <SafeIcon icon={FiExternalLink} className="w-4 h-4" />
                        </a>
                      )}
                    </div>
                  </motion.div>
                );
              })}
            </div>
          )}

          {/* Add Custom School Modal */}
          <AnimatePresence>
            {showAddModal && (
              <>
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="fixed inset-0 bg-black bg-opacity-50 z-50"
                  onClick={() => setShowAddModal(false)}
                />
                <motion.div
                  initial={{ opacity: 0, scale: 0.95 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.95 }}
                  className="fixed inset-0 z-50 flex items-center justify-center p-4"
                >
                  <div className="bg-white rounded-xl shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                    <div className="p-6">
                      <div className="flex items-center justify-between mb-6">
                        <h2 className="text-2xl font-space-grotesk font-bold text-secondary">
                          Add Custom School
                        </h2>
                        <button
                          onClick={() => setShowAddModal(false)}
                          className="text-gray-400 hover:text-gray-600 transition-colors"
                        >
                          <SafeIcon icon={FiX} className="w-6 h-6" />
                        </button>
                      </div>

                      <div className="space-y-4">
                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            School Name *
                          </label>
                          <input
                            type="text"
                            value={customSchoolForm.name}
                            onChange={(e) =>
                              setCustomSchoolForm({ ...customSchoolForm, name: e.target.value })
                            }
                            placeholder="e.g., UCLA"
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                          />
                        </div>

                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Division
                            </label>
                            <input
                              type="text"
                              value={customSchoolForm.division}
                              onChange={(e) =>
                                setCustomSchoolForm({
                                  ...customSchoolForm,
                                  division: e.target.value,
                                })
                              }
                              placeholder="e.g., Division I"
                              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Conference
                            </label>
                            <input
                              type="text"
                              value={customSchoolForm.conference}
                              onChange={(e) =>
                                setCustomSchoolForm({
                                  ...customSchoolForm,
                                  conference: e.target.value,
                                })
                              }
                              placeholder="e.g., Big Ten"
                              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Location
                          </label>
                          <input
                            type="text"
                            value={customSchoolForm.location}
                            onChange={(e) =>
                              setCustomSchoolForm({
                                ...customSchoolForm,
                                location: e.target.value,
                              })
                            }
                            placeholder="e.g., Los Angeles, CA"
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                          />
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Coach Name
                          </label>
                          <input
                            type="text"
                            value={customSchoolForm.coach}
                            onChange={(e) =>
                              setCustomSchoolForm({ ...customSchoolForm, coach: e.target.value })
                            }
                            placeholder="e.g., John Smith"
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                          />
                        </div>

                        <div className="grid md:grid-cols-2 gap-4">
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Email
                            </label>
                            <input
                              type="email"
                              value={customSchoolForm.email}
                              onChange={(e) =>
                                setCustomSchoolForm({ ...customSchoolForm, email: e.target.value })
                              }
                              placeholder="coach@school.edu"
                              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                              Phone
                            </label>
                            <input
                              type="tel"
                              value={customSchoolForm.phone}
                              onChange={(e) =>
                                setCustomSchoolForm({ ...customSchoolForm, phone: e.target.value })
                              }
                              placeholder="(555) 123-4567"
                              className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                            />
                          </div>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-gray-700 mb-2">
                            Staff Directory URL
                          </label>
                          <input
                            type="url"
                            value={customSchoolForm.staff_directory_url}
                            onChange={(e) =>
                              setCustomSchoolForm({
                                ...customSchoolForm,
                                staff_directory_url: e.target.value,
                              })
                            }
                            placeholder="https://..."
                            className="w-full px-3 py-2 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary"
                          />
                        </div>
                      </div>

                      <div className="flex items-center justify-end space-x-3 mt-6 pt-6 border-t border-gray-200">
                        <button
                          onClick={() => setShowAddModal(false)}
                          className="px-6 py-2 border border-gray-200 text-gray-600 rounded-lg hover:bg-gray-50 transition-colors"
                        >
                          Cancel
                        </button>
                        <button
                          onClick={handleAddCustomSchool}
                          disabled={saving}
                          className="bg-primary text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors btn-primary flex items-center space-x-2 disabled:opacity-50"
                        >
                          <SafeIcon icon={FiSave} className="w-4 h-4" />
                          <span>{saving ? 'Adding...' : 'Add School'}</span>
                        </button>
                      </div>
                    </div>
                  </div>
                </motion.div>
              </>
            )}
          </AnimatePresence>
        </div>
      </div>
    </FeatureGate>
  );
}

export default SchoolDirectory;